import { UserPermissions } from '@ravago/shared/page-data/models/config';

import { RfqStep } from '../models/request-for-quotation-step';

import { usePreviousValue } from './use-previous-value';

interface Props {
   initialStep?: RfqStep;
   userPermissions: UserPermissions;
}

const VERIFIED_FLOW = [RfqStep.QUOTATION_DETAILS, RfqStep.ADDRESS_DETAILS, RfqStep.SUMMARY, RfqStep.FINAL_MESSAGE];
const GUEST_OR_PENDING_FLOW = [RfqStep.QUOTATION_DETAILS, RfqStep.CONTACT_DETAILS, RfqStep.DELIVERY_AREA, RfqStep.SUMMARY, RfqStep.FINAL_MESSAGE];
const ANONYMOUS_FLOW = [
   RfqStep.QUOTATION_DETAILS,
   RfqStep.VERIFY_EMAIL,
   RfqStep.CONTACT_DETAILS,
   RfqStep.DELIVERY_AREA,
   RfqStep.SUMMARY,
   RfqStep.FINAL_MESSAGE
];

const getStepFlow = (userPermissions: UserPermissions) => {
   if (userPermissions.hasVerifiedPermission) {
      return VERIFIED_FLOW;
   }
   if (userPermissions.hasGuestPermission || userPermissions.hasPendingPermission) {
      return GUEST_OR_PENDING_FLOW;
   }
   return ANONYMOUS_FLOW;
};

export const useRequestForQuotationSteps = ({ userPermissions, initialStep = undefined }: Props) => {
   const steps = getStepFlow(userPermissions);
   const [currentStep, setCurrentStep, revertCurrentStep, reset] = usePreviousValue(initialStep ?? steps[0]);

   const currentStepIndex = currentStep === RfqStep.RECOGNIZED_EMAIL ? 1 : steps.indexOf(currentStep);

   const nextStep = (context?: { userExists: boolean }) => {
      if (currentStep === RfqStep.VERIFY_EMAIL && context) {
         if (context.userExists) {
            setCurrentStep(RfqStep.RECOGNIZED_EMAIL); // Optional, if it's shown
         } else {
            setCurrentStep(RfqStep.CONTACT_DETAILS);
         }
         return;
      }

      if (currentStep === RfqStep.RECOGNIZED_EMAIL) {
         return;
      }

      const next = steps[currentStepIndex + 1];
      if (next) setCurrentStep(next);
   };

   return {
      currentStep,
      currentStepIndex,
      nextStep,
      previousStep: revertCurrentStep,
      reset,
      stepCount: steps.length - 1 // Don't include the last step
   };
};
